"use client";

/**
 * RightsAndFees — a maker-facing explainer of (1) the Breakin' Bread philosophy,
 * (2) what rights a music maker keeps, and (3) exactly how much BB takes of a
 * sale and why. Shown on the profile page and the help page.
 *
 * The published platform cut is π % — imported from the single source of truth,
 * '@/lib/platform-fee' (PLATFORM_FEE_PERCENT = Math.PI). Never hard-code it here.
 */

import { Shield, Scale, HandCoins, Sprout, Wheat } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { PLATFORM_FEE_PERCENT } from "@/lib/platform-fee";
import { openDashboardTab } from "@/lib/dashboard-tabs";

/** π % as a precise 2-dp figure for prose, e.g. 3.14. */
const FEE_EXACT = +PLATFORM_FEE_PERCENT.toFixed(2);
/** What the maker keeps, e.g. 96.9. */
const MAKER_PCT = +(100 - PLATFORM_FEE_PERCENT).toFixed(1);

/** Published platform cut (π %). Kept for any importer that wants the number. */
export const PLATFORM_FEE_PCT = PLATFORM_FEE_PERCENT;

interface Props {
  className?: string;
  /** "full" (default) shows every block; "compact" trims the intro for tight spots. */
  variant?: "full" | "compact";
}

export function RightsAndFees({ className, variant = "full" }: Props) {
  const t = useTranslations("rightsFees");
  const tj = useTranslations("join");
  const b = (chunks: React.ReactNode) => <strong className="text-foreground">{chunks}</strong>;
  const em = (chunks: React.ReactNode) => <em>{chunks}</em>;
  return (
    <div className={cn("rounded-xl border border-border bg-card/60 p-5 sm:p-6 space-y-5", className)}>
      {/* Philosophy */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Wheat className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-bold text-foreground">{t("title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{t.rich("philosophy", { b })}</p>
        {/* Nobody owns it — anyone can join the board or the team (Gordon 2026-11-02). */}
        <p className="text-sm text-muted-foreground leading-relaxed">
          <strong className="text-foreground">{tj("title")}.</strong> {tj("body")}{" "}
          <Link href="/dashboard?tab=join" onClick={e => { if (openDashboardTab("join")) e.preventDefault(); }} className="font-semibold text-foreground underline underline-offset-2">{tj("cta")} →</Link>
        </p>
      </div>

      {/* Your rights */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-emerald-500" />
          <h4 className="text-base font-semibold text-foreground">{t("keepTitle")}</h4>
        </div>
        <ul className="text-sm text-muted-foreground space-y-1.5 list-none">
          {(["keep1", "keep2", "keep3", "keep4"] as const).map(k => (
            <li key={k} className="flex gap-2"><Scale className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /><span>{t.rich(k, { b })}</span></li>
          ))}
        </ul>
      </div>

      {/* The cut — π % */}
      <div className="space-y-3 rounded-lg bg-primary/5 border border-primary/20 p-4">
        <div className="flex items-center gap-2">
          <HandCoins className="h-5 w-5 text-primary" />
          <h4 className="text-base font-semibold text-foreground">{t("takeTitle")}</h4>
        </div>

        {/* The π seal is the fee. flex centring + leading-none frame the glyph;
            the π glyph's ink sits high in its em box, so a 2px downward nudge
            optically centres it in the circle. */}
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-primary/40 bg-primary/10">
            <span
              className="font-serif text-[2.75rem] leading-none text-primary"
              style={{ transform: "translateY(2px)" }}
              aria-hidden="true"
            >
              π
            </span>
          </div>
          <p className="text-sm text-foreground">
            <span className="text-2xl font-extrabold text-primary">π%</span>{" "}
            <span className="text-muted-foreground">(≈ {FEE_EXACT}%)</span> {t("cut")}
          </p>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">{t.rich("rest", { b, em, pct: MAKER_PCT })}</p>
        <div className="flex items-start gap-2 pt-1">
          <Sprout className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />
          <p className="text-xs text-muted-foreground leading-relaxed">{t.rich("why", { b, fee: FEE_EXACT })}</p>
        </div>
      </div>

      {variant === "full" && (
        <p className="text-xs text-muted-foreground/70">{t("merch")}</p>
      )}
    </div>
  );
}
